import { useCallback, useEffect, useMemo, useState } from 'react';

interface UseSearchFilterProps {
    initialQuery?: string | null;
    onQueryChange?: (query: string | null) => void;
}

const useSearchFilter = ({ initialQuery = null, onQueryChange }: UseSearchFilterProps = {}) => {
    const [query, setQuery] = useState(initialQuery);
    const [localQuery, setLocalQuery] = useState(initialQuery);

    const applyQuery = useCallback(() => {
        if (localQuery) {
            setQuery(localQuery);
        } else {
            setQuery(initialQuery);
        }

        if (onQueryChange) {
            onQueryChange(localQuery || initialQuery);
        }
    }, [localQuery, onQueryChange, initialQuery]);

    useEffect(() => {
        if (localQuery === '') {
            applyQuery();
        }
    }, [localQuery, applyQuery]);

    const setLocalQueryValue = useCallback((value: string) => {
        setLocalQuery(value);
    }, []);

    const clearQuery = useCallback(() => {
        setLocalQuery('');
        setQuery('');
        if (onQueryChange) {
            onQueryChange('');
        }
    }, [onQueryChange]);

    const searchActions = useMemo(
        () => ({
            setLocalQuery: setLocalQueryValue,
            applyQuery,
            clearQuery,
        }),
        [setLocalQueryValue, applyQuery, clearQuery],
    );

    return {
        query,
        localQuery,
        searchActions,
        setQuery,
        setLocalQuery: setLocalQueryValue,
        applyQuery,
        clearQuery,
    };
};

export default useSearchFilter;
